import { withStyles, WithStyles } from "@mui/styles";
import { DashboardStyleClasses } from "assets/jss/common-styles";
import { advFilterAddCommand, AdvFilterCommand, AdvFilterPicker, AdvFilterResult, AutoCompleteKeyWord } from "components/AdvFilter";
import { CmdActiveOnly, CmdAuthor, CmdTopicNoSubs, CmdOnlyInstitutional, CmdOnlyNotInstitutional, CmdPost, CmdQuestion, CmdTitle, CmdWithVideo, CmdSpectator, CommandLabels } from "components/AdvFilter/CommonCommands";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import { PublicationType } from "models/ApiEnums";
import { DashboardInstitutionalFilterOption, DashboardPublicationAnalysisFilter, DashboardPublicationAnalysisSummaryColumn } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import PublicationAnalysisFilter from "views/Dashboards/PublicationAnalysisDashboard/PublicationAnalysisFilter";
import { DashboardPublicationAnalysisPublicationsGraph } from "views/Dashboards/PublicationAnalysisDashboard/PublicationAnalysisPublications";
import { DashboardPublicationAnalysisSocialGraph } from "views/Dashboards/PublicationAnalysisDashboard/PublicationAnalysisSocial";
import PublicationAnalysisTiles from "views/Dashboards/PublicationAnalysisDashboard/PublicationAnalysisTiles";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    activeTitle: DashboardPublicationAnalysisSummaryColumn;
    filter: DashboardPublicationAnalysisFilter;
    advFilters: AdvFilterResult[];
}

type Props = WithStyles<typeof DashboardStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class PublicationAnalysisDashboard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            activeTitle: DashboardPublicationAnalysisSummaryColumn.Publications,
            filter: null,
            advFilters: []
        };
    }

    ///
    private handleSelectUser = (topicId: string, name: string) => {
        this.setState(({ advFilters }) => {

            advFilters = advFilterAddCommand(this.getCommands(), advFilters, CmdAuthor, topicId, name);

            return { advFilters };
        }, () => this.props.snackbar.showMessage(this.props.t("Views.Dashboards.Common.FilteringPubAuthor", { name: name })));
    };

    ///
    private handleSelectTopic = (topicId: string, name: string, thumbnailUrl: string) => {

        this.setState(({ advFilters }) => {

            advFilters = advFilterAddCommand(this.getCommands(), advFilters, AutoCompleteKeyWord, { key: topicId, label: name, thumbnailUrl: thumbnailUrl } as AutoCompleteDataModel, name);

            return { advFilters };
        }, () => this.props.snackbar.showMessage(this.props.t("Views.Dashboards.Common.FilteringTopic", { name: name })));
    };

    ///
    private getFilter(): DashboardPublicationAnalysisFilter {
        const { advFilters } = this.state;

        var topic = advFilters.find(p => p.keyword == AutoCompleteKeyWord);
        const includeSubtopics = advFilters.find(p => p.keyword == CmdTopicNoSubs) == null;

        const author = advFilters.find(p => p.keyword == CmdAuthor);
        const viewer = advFilters.find(p => p.keyword == CmdSpectator);

        const publicationTypes: PublicationType[] = [];
        if (advFilters.find(p => p.keyword == CmdPost)) publicationTypes.push(PublicationType.Post);
        if (advFilters.find(p => p.keyword == CmdQuestion)) publicationTypes.push(PublicationType.Question);

        const onlyNonInstitutional = advFilters.findIndex(p => p.keyword == CmdOnlyNotInstitutional) > -1;
        const onlyInstitutional = advFilters.findIndex(p => p.keyword == CmdOnlyInstitutional) > -1;
        const withVideo = advFilters.find(p => p.keyword == CmdWithVideo);
        const withTitle = advFilters.find(p => p.keyword == CmdTitle);
        const activeOnly = advFilters.find(p => p.keyword == CmdActiveOnly);

        return {
            topicId: topic == null
                ? null
                : topic.value.key,

            includeSubtopics: includeSubtopics,

            authorId: author == null ? null : author.value,

            viewerId: viewer == null ? null : viewer.value,

            publicationTypes: publicationTypes.length == 0 ? null : publicationTypes,

            institutionalOption: onlyNonInstitutional
                ? DashboardInstitutionalFilterOption.OnlyNonInstitutional
                : onlyInstitutional
                    ? DashboardInstitutionalFilterOption.OnlyInstitutional
                    : DashboardInstitutionalFilterOption.Both,

            withTitle: withTitle == null ? null : withTitle.value,
            withVideo: withVideo == null ? null : true,
            activeOnly: activeOnly == null ? null : true
        };
    }

    ///
    private getCommands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdTopicNoSubs, displayName: cmdLabels[CmdTopicNoSubs], group: 1, canRepeat: false, },
            { keyword: CmdTitle, displayName: cmdLabels[CmdTitle], picker: AdvFilterPicker.Text, group: 1, canRepeat: false, },

            { keyword: CmdAuthor, displayName: cmdLabels[CmdAuthor], picker: AdvFilterPicker.User, group: 2, canRepeat: false },
            { keyword: CmdSpectator, displayName: cmdLabels[CmdSpectator], picker: AdvFilterPicker.User, group: 2, canRepeat: false },

            { keyword: CmdPost, displayName: cmdLabels[CmdPost], picker: null, group: 3 },
            { keyword: CmdQuestion, displayName: cmdLabels[CmdQuestion], picker: null, group: 3 },

            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo], picker: null, group: 4, canRepeat: false },

            { keyword: CmdOnlyNotInstitutional, displayName: cmdLabels[CmdOnlyNotInstitutional], group: 5, optionGroup: "Institutional" },
            { keyword: CmdOnlyInstitutional, displayName: cmdLabels[CmdOnlyInstitutional], group: 5, optionGroup: "Institutional" },
            { keyword: CmdActiveOnly, displayName: cmdLabels[CmdActiveOnly], group: 5 },
        ];
    }

    ///
    render() {
        const { advFilters, activeTitle } = this.state;
        const filter = this.getFilter();

        return (
            <div className={this.props.classes.dashboardLargeContainer}>

                <PublicationAnalysisFilter
                    {...this.props}
                    advFilters={advFilters}
                    commands={this.getCommands()}
                    onFilterChange={(advFilters) => this.setState({ advFilters })}
                />

                <PublicationAnalysisTiles
                    {...this.props}
                    activeTile={activeTitle}
                    filter={filter}
                    onChange={
                        (tile) => {
                            if (tile == DashboardPublicationAnalysisSummaryColumn.Publications)
                                this.setState({ activeTitle: tile });
                            else
                                this.setState({ activeTitle: tile });

                        }
                    }
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="warning">

                        {
                            activeTitle == DashboardPublicationAnalysisSummaryColumn.Publications
                                ? <DashboardPublicationAnalysisPublicationsGraph {...this.props} tile={activeTitle} filter={filter} onSelectUser={this.handleSelectUser} onSelectTopic={this.handleSelectTopic} />
                                : <DashboardPublicationAnalysisSocialGraph {...this.props} tile={activeTitle} filter={filter} onSelectUser={this.handleSelectUser} onSelectTopic={this.handleSelectTopic} />
                        }

                    </MuiPaper>
                </MuiContainer>

            </div>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(DashboardStyleClasses)(PublicationAnalysisDashboard)));